/**
 * Coloca la textura de una encimera dentro del atlas de referencia (3200 × 1200 mm)
 * para que todas las encimeras de un mismo conjunto se lean como una única pieza.
 *
 * El atlas es un rectángulo de material real: 1 unidad UV en U = 3200 mm a lo ancho,
 * 1 unidad UV en V = 1200 mm de fondo. Cada encimera muestrea un trozo de ese rectángulo;
 * la continuidad consiste en que cada una muestree EL trozo que le toca por su sitio real.
 *
 * Dos modos, porque una fila y una isla no se recorren igual:
 *
 * - `row` (lo de siempre): la fila se "desenrolla" sumando anchos. Es lo correcto para una
 *   línea o para una L, donde la veta debe seguir la encimera al doblar la esquina: lo que
 *   cuenta es la longitud desarrollada, no la posición en planta.
 *
 * - `island`: el grupo tiene varias filas paralelas unidas por sus caras front/back (isla,
 *   doble fila, península). Ahí no hay un recorrido lineal único: las filas van una al lado
 *   de la otra en profundidad, así que la posición se mide PROYECTANDO en planta sobre los
 *   ejes de la isla. La fila de delante ocupa la banda V baja, la de detrás la alta, y el
 *   conjunto sale de una sola plancha.
 *
 * Los ejes de la isla los marca su primer mueble. Una fila girada 180° (la de detrás, que
 * mira al otro lado) se coloca sola: su esquina de origen cae en el extremo alto de la isla
 * y su textura se gira 180°, con lo que barre hacia atrás y encaja con la de delante.
 *
 * Un brazo PERPENDICULAR dentro del grupo (una península que dobla) no entra en modo isla:
 * girar la UV 90° en un atlas que no es cuadrado deformaría la escala de la veta
 * (3200 ≠ 1200). Esos vuelven a modo `row`, es decir, siguen siendo su propia línea.
 */

/** Ancho del atlas de encimera (mm). Calibrado; no tocar sin recalibrar el resto. */
export const ENCIMERA_ATLAS_WIDTH_MM = 3200
/** Fondo del atlas de encimera (mm). */
export const ENCIMERA_ATLAS_HEIGHT_MM = 1200

/**
 * Sentido del eje V respecto al fondo del mueble. +1 = la V crece hacia el frente (el eje Z
 * local del mueble). Si en una isla la fila de detrás sale con la veta espejada respecto a
 * la de delante, el culpable es este signo: ponerlo a -1.
 */
const ENCIMERA_UV_V_AXIS_SIGN = 1

/** Un brazo con más de este seno respecto al eje de la isla se trata como línea aparte. */
const PERPENDICULAR_SIN_THRESHOLD = 0.15

const DEG_TO_RAD = Math.PI / 180
const RAD_TO_DEG = 180 / Math.PI

const DEBUG_ENCIMERA_UV = false

/** Lo mínimo que necesita el cálculo de cada entrada de `getContItemsForEncimeras`. */
export type EncimeraLayoutEntry = {
  id: string
  position: { x: number; y: number; z: number }
  width: number
  thickness: number
  /** Grados, tal como los devuelve `getInitialAngleDegrees` */
  rotation: number
  orderIndex: number
  groupId?: string | number
  groupShape?: string
  rowIndex?: number
  rowCount?: number
}

export type EncimeraUvPlacement = {
  /** Desplazamiento dentro del atlas, en unidades UV */
  offsetU: number
  offsetV: number
  /** Grados que hay que SUMAR a la rotación del acabado */
  extraRotationDeg: number
  mode: 'row' | 'island'
}

type Vec2 = { x: number; z: number }

const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z

/**
 * Ejes de mundo del mueble en el plano XZ.
 *
 * `getInitialAngleDegrees` devuelve el ángulo INVERSO al giro en Y de la matriz (mide el
 * ángulo del eje X con signo `-sign(cross.y)`), así que para r grados el eje X local cae en
 * (cos r, sin r) y el eje Z local —el fondo— en (-sin r, cos r).
 */
function getEntryAxes(rotationDeg: number): { xAxis: Vec2; zAxis: Vec2 } {
  const r = rotationDeg * DEG_TO_RAD
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  return { xAxis: { x: cos, z: sin }, zAxis: { x: -sin, z: cos } }
}

/** Las 4 esquinas de la huella en planta, desde el origen (esquina izquierda-trasera). */
function getFootprintCorners(entry: EncimeraLayoutEntry): Vec2[] {
  const { xAxis, zAxis } = getEntryAxes(entry.rotation)
  const origin: Vec2 = { x: entry.position.x, z: entry.position.z }
  const w = entry.width || 0
  const t = entry.thickness || 0
  return [
    origin,
    { x: origin.x + xAxis.x * w, z: origin.z + xAxis.z * w },
    { x: origin.x + zAxis.x * t, z: origin.z + zAxis.z * t },
    { x: origin.x + xAxis.x * w + zAxis.x * t, z: origin.z + xAxis.z * w + zAxis.z * t },
  ]
}

/** Modo fila: la posición es la suma de anchos anteriores del tramo. */
function getRowPlacement(group: EncimeraLayoutEntry[], itemIndex: number): EncimeraUvPlacement {
  let accumulatedMm = 0
  for (let i = 0; i < itemIndex; i++) {
    accumulatedMm += group[i]?.width || 0
  }
  return {
    offsetU: accumulatedMm / ENCIMERA_ATLAS_WIDTH_MM,
    offsetV: 0,
    extraRotationDeg: 0,
    mode: 'row',
  }
}

/**
 * Trozo del atlas que le toca a `fatherId`.
 *
 * @param groups grupos de `getContItemsForEncimeras` (uno por FILA, no por isla)
 * @returns null si el mueble no aparece en ningún grupo
 */
export function getEncimeraUvPlacement(
  groups: EncimeraLayoutEntry[][],
  fatherId: string,
): EncimeraUvPlacement | null {
  const group = groups.find((g) => g.some((entry) => entry.id === fatherId))
  if (!group || group.length === 0) return null

  const itemIndex = group.findIndex((entry) => entry.id === fatherId)
  if (itemIndex === -1) return null

  const target = group[itemIndex]
  const rowPlacement = getRowPlacement(group, itemIndex)

  // Una sola fila: no hay nada con lo que compartir plancha en profundidad.
  if (target.groupId == null || (target.rowCount ?? 1) <= 1) return rowPlacement

  // La isla es el grupo conexo entero: todas las filas que comparten groupId.
  const islandById = new Map<string, EncimeraLayoutEntry>()
  for (const candidate of groups) {
    for (const entry of candidate) {
      if (entry.groupId === target.groupId) islandById.set(entry.id, entry)
    }
  }
  const island = [...islandById.values()]
  const rowIndexes = new Set(island.map((entry) => entry.rowIndex ?? 0))
  if (island.length < 2 || rowIndexes.size < 2) return rowPlacement

  // Ejes de la isla: los del primer mueble de su primera fila.
  const reference = island.reduce((best, entry) => {
    const row = entry.rowIndex ?? 0
    const bestRow = best.rowIndex ?? 0
    if (row !== bestRow) return row < bestRow ? entry : best
    return entry.orderIndex < best.orderIndex ? entry : best
  }, island[0])

  const referenceAxes = getEntryAxes(reference.rotation)
  const uAxis = referenceAxes.xAxis
  const vAxis: Vec2 = {
    x: referenceAxes.zAxis.x * ENCIMERA_UV_V_AXIS_SIGN,
    z: referenceAxes.zAxis.z * ENCIMERA_UV_V_AXIS_SIGN,
  }

  // El brazo perpendicular de una península no comparte plancha: el atlas no es cuadrado,
  // girar su UV 90° cambiaría la escala de la veta. Vuelve a ser su propia línea.
  const targetAxes = getEntryAxes(target.rotation)
  const alongU = dot(targetAxes.xAxis, uAxis)
  const alongV = dot(targetAxes.xAxis, vAxis)
  if (Math.abs(alongV) > PERPENDICULAR_SIN_THRESHOLD) return rowPlacement

  // Origen de la isla: la esquina de su huella con menor U y menor V.
  let minU = Infinity
  let minV = Infinity
  for (const entry of island) {
    for (const corner of getFootprintCorners(entry)) {
      const u = dot(corner, uAxis)
      const v = dot(corner, vAxis)
      if (u < minU) minU = u
      if (v < minV) minV = v
    }
  }
  if (!Number.isFinite(minU) || !Number.isFinite(minV)) return rowPlacement

  // La UV local del mesh arranca en la esquina de origen del mueble, así que su sitio en el
  // atlas es la proyección de esa esquina. En una fila girada 180° esa esquina es la del
  // extremo alto, y la textura (girada también 180°) barre hacia atrás desde ahí.
  const origin: Vec2 = { x: target.position.x, z: target.position.z }
  const offsetU = (dot(origin, uAxis) - minU) / ENCIMERA_ATLAS_WIDTH_MM
  const offsetV = (dot(origin, vAxis) - minV) / ENCIMERA_ATLAS_HEIGHT_MM

  // El giro de la textura es el del mueble respecto a los ejes de la isla. La matriz UV de
  // three aplica R(-rotation), así que para orientar la veta como la isla hay que pasar -θ.
  const thetaDeg = Math.atan2(alongV, alongU) * RAD_TO_DEG

  if (DEBUG_ENCIMERA_UV) {
    console.log('🪵 [encimera UV] isla', {
      item: fatherId,
      forma: target.groupShape,
      fila: `${target.rowIndex}/${target.rowCount}`,
      referencia: reference.id,
      offsetU: offsetU.toFixed(4),
      offsetV: offsetV.toFixed(4),
      giro: Math.round(thetaDeg),
    })
  }

  return {
    offsetU,
    offsetV,
    extraRotationDeg: -thetaDeg,
    mode: 'island',
  }
}
